import React, { useCallback, useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import { Container, Timer, SMSResend } from "./styles";
import { useSnackbar } from "notistack";

interface ICountdownData {
  expireTime: number;
  isExpired: (expired: boolean) => void;
  resendCode: (type: string) => void;
  resendLabel?: string;
  /** When set, replaces the default bottom snackbar (e.g. inline card alert). */
  onExpired?: () => void;
  /**
   * When true, skip the cooldown and enable "Reenviar" immediately
   * (e.g. previous OTP delivery failed — do not fake a success timer).
   */
  unlockResendImmediately?: boolean;
}

/**
 * Cooldown UI for OTP resend.
 * HARD RULE: timer expiry only unlocks the manual "Reenviar" button —
 * it must NEVER call resendCode by itself (that caused SMS/email spam loops).
 */
export const Countdown: React.FC<ICountdownData> = ({
  expireTime,
  resendCode,
  isExpired,
  resendLabel,
  onExpired,
  unlockResendImmediately = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const duration = Math.max(1, Math.floor(expireTime) || 45);
  const [remaining, setRemaining] = useState(
    unlockResendImmediately ? 0 : duration
  );
  const [canResend, setCanResend] = useState(unlockResendImmediately);
  /** Bumps when the user manually resends so the interval effect restarts cleanly. */
  const [cycle, setCycle] = useState(0);
  const expiredNotifiedRef = useRef(unlockResendImmediately);
  const skipExpiredNoticeRef = useRef(unlockResendImmediately);
  const resendLockRef = useRef(false);
  const onExpiredRef = useRef(onExpired);
  const isExpiredRef = useRef(isExpired);

  onExpiredRef.current = onExpired;
  isExpiredRef.current = isExpired;

  const notifyExpired = useCallback(() => {
    if (expiredNotifiedRef.current) return;
    expiredNotifiedRef.current = true;
    setCanResend(true);
    isExpiredRef.current?.(true);

    if (onExpiredRef.current) {
      onExpiredRef.current();
      return;
    }

    enqueueSnackbar(intl.get("auth.tokenExpired"), {
      variant: "warning",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
  }, [enqueueSnackbar]);

  // One interval per cooldown cycle. Expiry unlocks resend only — never auto-sends.
  useEffect(() => {
    if (canResend) return undefined;

    const intervalId = window.setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [canResend, cycle]);

  useEffect(() => {
    if (remaining !== 0) return;

    // Initial "delivery failed" unlock — enable resend without "token expired" noise.
    if (skipExpiredNoticeRef.current) {
      skipExpiredNoticeRef.current = false;
      setCanResend(true);
      isExpiredRef.current?.(true);
      return;
    }

    notifyExpired();
  }, [remaining, notifyExpired]);

  useEffect(() => {
    // Mirror legacy API: isExpired(true) when resend is unlocked.
    isExpiredRef.current?.(canResend);
  }, [canResend]);

  const handleResendClick = useCallback(() => {
    if (!canResend || resendLockRef.current) return;
    resendLockRef.current = true;

    // Restart cooldown immediately so a second click cannot fire another send.
    skipExpiredNoticeRef.current = false;
    expiredNotifiedRef.current = false;
    setCanResend(false);
    setRemaining(duration);
    setCycle((n) => n + 1);
    isExpiredRef.current?.(false);

    try {
      resendCode("resend");
    } finally {
      window.setTimeout(() => {
        resendLockRef.current = false;
      }, 1000);
    }
  }, [canResend, duration, resendCode]);

  const minuteTimer = Math.floor(remaining / 60);
  const secondTimer = remaining % 60;
  const isActive = !canResend && remaining > 0;

  return (
    <Container>
      <Timer isActive={isActive}>
        {minuteTimer >= 10 ? minuteTimer.toString() : `0${minuteTimer}`}
        :
        {secondTimer >= 10 ? secondTimer.toString() : `0${secondTimer}`}
      </Timer>
      <SMSResend
        isActive={isActive}
        disabled={!canResend}
        type="button"
        onClick={handleResendClick}
      >
        {resendLabel || intl.get("auth.resendSms")}
      </SMSResend>
    </Container>
  );
};
